import React from 'react';
import { hot } from 'react-hot-loader';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import LazyImage from '../../hocs/Lazy/LazyImage';
import Slider from '../../hocs/Slider/Slider';
import Countdown from './Countdown/Countdown';
import Customizer from './Customizer/Customizer';
import FreeHTML from './FreeHTML/FreeHTML';
import Instagram from './Instagram/Instagram';
import Media from './Media/Media';
import Newsletter from '../Footer/Newsletter';
import Products from './Products/Products';
import SizeSelector from './SizeSelector/SizeSelector';
import Spacer from './Spacer/Spacer';
import Text from './Text/Text';
import TrustBox from './TrustBox/TrustBox';
import { imageSizeTranslation } from '../../utils/image';
import { translate } from '../../utils/locale';
import { urlify } from '../../utils/string';

import s from './TemplateParserDeprecated.scss';

const TemplateInner = ({ isProduct, last, section, inline, setup, country, ...props }) => {
	const standardAttributes = { setup, isProduct, ...props, isDeprecatedTemplate: true };
	const { margin = {}, padding = {}, properties = {}, background = {}, layout = {} } = section;
	const sectionCSS = {
		textAlign: layout?.fullWidth ? properties?.align?.horizontal : 'center',
	};
	const responsiveImages = background?.image ? imageSizeTranslation('full', background.image, true) : {};
	const heightType = layout?.cropHeight ? 'height' : 'minHeight';
	const mobileHeightSet = layout?.mediaHeight?.mobile && setup.breakPoint === 'mobile';
	const desktopHeightSet = layout?.mediaHeight?.desktop && setup.breakPoint !== 'mobile';

	if (!section.column) return null;

	if (margin?.top || margin?.top === 0) sectionCSS.marginTop = `${parseInt(margin.top, 10)}px`;
	if (padding?.top || padding?.top === 0) sectionCSS.paddingTop = `${parseInt(padding.top, 10)}px`;
	if (margin?.bottom || margin?.bottom === 0) sectionCSS.marginBottom = `${parseInt(margin.bottom, 10)}px`;
	if (padding?.bottom || padding?.bottom === 0) sectionCSS.paddingBottom = `${parseInt(padding.bottom, 10)}px`;

	if (mobileHeightSet) sectionCSS[heightType] = `${parseInt(layout.mediaHeight.mobile, 10)}vh`;
	if (desktopHeightSet) sectionCSS[heightType] = `${parseInt(layout.mediaHeight.desktop, 10)}vh`;

	if (background?.color) sectionCSS.backgroundColor = background.color;

	const columns =	section.column.map((column) => {
		if (!column.template) return null;
		const components = {
			countdown: Countdown,
			customizer: Customizer,
			freeHtml: FreeHTML,
			instagram: Instagram,
			media: Media,
			newsletter: Newsletter,
			products: Products,
			sizeSelector: SizeSelector,
			spacer: Spacer,
			text: Text,
			trustBox: TrustBox,
		};

		const Template = components[column.template];
		const columnCSS = {
			verticalAlign: properties?.align?.vertical,
		};
		if (section.column.length > 1 && desktopHeightSet) {
			columnCSS.position = 'relative';
			columnCSS.height = `${parseInt(layout.mediaHeight.desktop, 10)}vh`;
		}
		if (section.column.length > 1 && mobileHeightSet) {
			columnCSS.position = 'relative';
			columnCSS.height = `${parseInt(layout.mediaHeight.mobile, 10)}vh`;
		}

		if (!Template) {
			console.log(`Missing template for: ${column.template}`);
			return null;
		}

		return (
			<div key={column.key} className={[s.section_inner_column, 'u_columnTight', `u_template__${column.template}`, `u_widthTight__${column?.position?.width || 12}`, (column?.position?.offset ? `u_offsetTight__${column.position.offset}` : null)].join(' ')} style={columnCSS}>
				<ErrorBoundary message={`CMS column | template: ${column?.template} key: ${column?.key}`} debugData={column}>
					<Template content={column[column.template]} sectionHeight={[heightType, sectionCSS[heightType]]} uid={column.key} columnWidth={column?.position?.width || 12} keepColumnsMobile={layout?.mobileLayout === 'row'} {...standardAttributes} />
				</ErrorBoundary>
			</div>
		);
	});

	if (inline) {
		return columns;
	}

	const sectionProps = {};
	const sectionInnerCss = { textAlign: properties?.align?.horizontal };
	const sectionTitle = section?.sectionTitle?.text ? translate(setup, section.sectionTitle.text) : '';
	const horizontalPos = {
		left: '13%',
		right: '87%',
	};
	if (sectionTitle) sectionProps.id = urlify(sectionTitle);

	return (
		<div {...sectionProps} className={[s.section, (layout?.cropHeight && (mobileHeightSet || desktopHeightSet) ? 'u_sectionCrop' : null), (properties?.gridType === 'spaced' ? 'u_gridSpaced' : null), ...(sectionCSS[heightType] ? [s.section__alignContents, s[`section__alignContents__${properties?.align?.vertical}`]] : [])].join(' ')} style={sectionCSS}>
			{ sectionCSS[heightType] && <div className={s.section_heightAlign} style={{ [heightType]: sectionCSS[heightType] }} /> }
			{
				background?.image && (
					<LazyImage {...responsiveImages} className={[s.section_bgImage, (!background.settings.maintainAspectRatio ? s.section_bgImage__fill : null)]} alt={background.image.alt} style={{ objectPosition: `${horizontalPos[background.settings.origin.horizontal] || 'center'} ${background.settings.origin.vertical === 'middle' ? 'center' : background.settings.origin.vertical}` }} />
				)
			}
			<div className={[s.section_inner, (layout?.mobileLayout === 'row' ? 'u_mobileAsDesktop' : null), (layout?.fullWidth ? 'u_fullWidth' : null)].join(' ')} style={sectionInnerCss}>
				{ columns }
			</div>
		</div>
	);
};

const TemplateParserDeprecated = ({ sections, setup, currentPath = false, canonical = false, ...props }) => {
	if (!sections) return null;
	let slides = [];
	let sliderAttrs = {
		showArrows: false,
		autoplay: 4000,
		screenMinWidthDisable: 750,
	};
	let emptySlides = false;
	let first = true;
	const templateContents = [];
	const { statics: staticText, country } = setup;
	canonical = currentPath ? `${currentPath}/${canonical}` : false;
	const standardAttributes = { setup, staticText, country, canonical, ...props };

	sections.forEach((section, i) => {
		if (emptySlides) {
			slides = [];
			emptySlides = false;
			sliderAttrs = {
				showArrows: false,
				autoplay: 4000,
				screenMinWidthDisable: 750,
			};
		}
		const { restrict = {} } = section;
		if (restrict?.countries && restrict.countries.indexOf(country.toUpperCase()) !== -1) return;
		if (restrict?.mobile && setup.breakPoint === 'mobile') return;
		if (restrict?.desktop && setup.breakPoint !== 'mobile') return;

		if (section?.properties?.isSlide) {
			if (first && section?.properties?.sliderProperties) {
				sliderAttrs.showArrows = section.properties.sliderProperties.arrows;
				if (!section.properties.sliderProperties.pagination) sliderAttrs.showNav = false;
				if (!section.properties.sliderProperties.freeOnMobile) delete sliderAttrs.screenMinWidthDisable;
				if (!section.properties.sliderProperties.autoSlide) sliderAttrs.autoplay = false;
				if (section.properties.sliderProperties.autoSlide && parseInt(section.properties.sliderProperties.delay, 10) > 500) sliderAttrs.autoplay = section.properties.sliderProperties.delay;
				if (section.properties.sliderProperties.color) sliderAttrs.color = section.properties.sliderProperties.color;
			}

			slides.push(<TemplateInner key={section.key} first={first} last={sections.length - 1 === i} section={section} {...standardAttributes} />);
			first = false;
		}

		if (slides.length > 0) {
			if (!section?.properties?.isSlide || sections.length === i + 1) {
				// Has slides and this section is not in slider or Last section create slider or has a different insertion point.
				emptySlides = true;
				templateContents.push(<Slider key={`${section.key}_slider`} infinite {...sliderAttrs}>{slides.map((slide) => slide)}</Slider>);
			}
		}

		if (!section?.properties?.isSlide) {
			templateContents.push(<TemplateInner key={section.key} first={first} last={sections.length - 1 === i} section={section} {...standardAttributes} />);
			first = false;
		}
	});

	return templateContents;
};

export default hot(module)(TemplateParserDeprecated);
